import { DOCUMENT, NgClass, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, viewChild } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import {
  MenuEventArgs,
  MenuModule,
  SidebarModule,
  ToolbarModule,
  TreeViewModule,
} from '@syncfusion/ej2-angular-navigations';
import { RouteConstants } from '../shared/constants/route.constant';
import { InteractionsService } from '@goatsports/core/data-access';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, map, of, startWith, switchMap } from 'rxjs';
import { DropDownButtonModule } from '@syncfusion/ej2-angular-splitbuttons';
import { AppBarModule } from '@syncfusion/ej2-angular-navigations';
import { ProfileMenuItem } from '../shared/models/profile-menu-item';
import { ErrorMessagesComponent } from '../shared/components/error-messages/error-messages.component';
import { TooltipModule } from '@syncfusion/ej2-angular-popups';
import { environment } from '../../environments/environment';
import { ShellService } from './shell.service';
import { IconStatusComponent } from './icon-status/icon-status.component';
import { CoachType, RoleType } from '@goatsports/core/data-access';
import { InteractionChangesService } from '../shared/services/interaction-changes.service';
import { ContactsAvailabilityService } from '../shared/services/contacts-availability.service';
import { LegalAgreements, ShortenCounterPipe } from '@goatsports/shared/util';
import { ShowToolbarButtonsPipe } from './show-toolbar-buttons.pipe';
import { NotificationsListComponent } from '../shared/components/notifications-list/notifications-list.component';
import { ShowRoleBasedNavigationPipe } from './show-role-based-navigation.pipe';
import { ROLE_PERMISSIONS } from '../shared/functions/role-permissions';
import { TranslocoModule } from '@jsverse/transloco';
import { ButtonModule } from '@syncfusion/ej2-angular-buttons';
import { AdDirective } from '@goatsports/shared/util';
import { OrganizationAdminOrganizationsStore } from './organization-switch/organization-admin-organizations.store';
import { OrganizationSwitchDialogService } from './organization-switch/organization-switch-dialog.service';
import { ActiveOrganizationWatchService } from '../shared/services/active-organization-watch.service';
import { OrganizationChangedDialogComponent } from './organization-switch/organization-changed-dialog.component';

@Component({
  selector: 'goatsports-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    NgOptimizedImage,
    TranslocoModule,
    TooltipModule,
    SidebarModule,
    ToolbarModule,
    TreeViewModule,
    ButtonModule,
    AppBarModule,
    RouterModule,
    ShortenCounterPipe,
    ShowToolbarButtonsPipe,
    ShowRoleBasedNavigationPipe,
    DropDownButtonModule,
    MenuModule,
    NotificationsListComponent,
    ErrorMessagesComponent,
    IconStatusComponent,
    AdDirective,
    OrganizationChangedDialogComponent,
  ],
  providers: [ShellService],
})
export default class ShellComponent {
  private readonly router = inject(Router);
  private readonly shellService = inject(ShellService);
  private readonly document = inject(DOCUMENT);
  private readonly orgStore = inject(OrganizationAdminOrganizationsStore);
  private readonly orgSwitchDialog = inject(OrganizationSwitchDialogService);
  private readonly activeOrganizationWatch = inject(ActiveOrganizationWatchService);
  readonly organizationMismatch = this.activeOrganizationWatch.mismatch;
  private readonly contactsAvailability = inject(ContactsAvailabilityService);

  readonly contactsEnabled = this.contactsAvailability.isEnabled;

  adHost = viewChild.required(AdDirective);

  readonly ProfileMenuItem = ProfileMenuItem;
  readonly RoleType = RoleType;

  // Map RoleType -> existing i18n key (already translated in all locales)
  // so the user-pill role label reuses one source of truth instead of
  // introducing parallel camelCase keys.
  private readonly roleLabelKeys: Partial<Record<RoleType, string>> = {
    [RoleType.Coach]: 'Coach',
    [RoleType.CustomClient]: 'Custom Client',
    [RoleType.PremiumClient]: 'Premium Client',
    [RoleType.FreemiumClient]: 'Freemium Client',
    [RoleType.OrganizationAdmin]: 'Organization Admin',
    [RoleType.SuperAdmin]: 'Super Admin',
    [RoleType.System]: 'System',
    [RoleType.Translator]: 'Translator',
  };

  currentUserRole = this.shellService.currentUserRole;
  // Logo target: first nav item of the current role (Dashboard for all roles
  // except Translator, whose only page is Settings).
  readonly homeRoute =
    ROLE_PERMISSIONS[this.shellService.currentUserRole]?.[0]?.id ?? `/${RouteConstants.DASHBOARD_ROUTE}`;
  currentUserFullName = this.shellService.currentUserFullName;
  currentUserInitials = this.shellService.currentUserInitials;
  coachType = this.shellService.coachType;

  readonly roleLabelKey = computed(() => {
    const role = this.currentUserRole;
    if (role === RoleType.Coach && this.coachType() === CoachType.Head) {
      return 'Head Coach';
    }
    return (role && this.roleLabelKeys[role]) || '';
  });
  menuItems = this.shellService.menuItems;
  unreadMessagesCount = this.shellService.unreadMessagesCount;

  private readonly interactionsService = inject(InteractionsService);

  private readonly interactionChanges = inject(InteractionChangesService);

  /**
   * Follow-ups now overdue. A badge on your own workload, so it never follows the calendar
   * look-through to somebody else.
   *
   * Refetched whenever an interaction is written and whenever the active organisation
   * changes. The shell survives navigation, so a one-shot request left the badge showing a
   * backlog the coach had just cleared.
   */
  readonly overdueCount = toSignal(
    toObservable(computed(() => ({ org: this.orgStore.activeOrganizationUid(), enabled: this.contactsEnabled() })))
      .pipe(
        switchMap(({ enabled }) =>
          enabled
            ? this.interactionChanges.changed$.pipe(
                startWith(undefined),
                map(() => true),
              )
            : of(false),
        ),
      )
      .pipe(
        switchMap((enabled) =>
          !enabled
            ? of(0)
            : this.interactionsService
                .interactionsOverdueGet(
                  Intl.DateTimeFormat().resolvedOptions().timeZone,
                  undefined,
                  false,
                  undefined,
                  undefined,
                  undefined,
                  undefined,
                  undefined,
                  0,
                  1,
                )
                .pipe(
                  map((res) => res.totalCount ?? 0),
                  catchError(() => of(0)),
                ),
        ),
      ),
    { initialValue: 0 },
  );

  unreadNotificationsCount = this.shellService.unreadNotificationsCount;

  isErrorThrown = this.shellService.isErrorThrown;
  language = this.shellService.language;
  routerUrl = this.shellService.routerUrl!;
  activeOrganizationName = this.orgStore.activeOrganizationName;
  hasMultipleOrganizations = this.orgStore.hasMultipleOrganizations;

  navigate(url: string) {
    this.router.navigate([url]);
  }

  openOrganizationSwitchDialog() {
    this.orgSwitchDialog.open(this.adHost());
  }

  constructor() {
    // Preload organizations for admins so active name is ready
    if (this.currentUserRole === this.RoleType.OrganizationAdmin) {
      this.orgStore.loadOrganizationsForCurrentAdmin();
      this.activeOrganizationWatch.start();
    }
  }

  private navigateToChangePassword() {
    const url = new URL(`${environment.authorityPortalUrl}/${RouteConstants.CHANGE_PASSWORD_ROUTE}`);
    url.searchParams.append('email', this.shellService.currentUserEmail);
    url.searchParams.append('returnUrl', this.document.baseURI);
    url.searchParams.append('language', this.language());
    url.searchParams.append('source', 'coach-portal');

    location.href = url.href;
  }

  private navigateToProfile() {
    this.router.navigate([RouteConstants.PROFILE_ROUTE]);
  }

  executeAction(event: MenuEventArgs) {
    switch (event.item.text) {
      case ProfileMenuItem.Profile:
        this.navigateToProfile();
        break;
      case ProfileMenuItem.ChangePassword:
        this.navigateToChangePassword();
        break;
      case ProfileMenuItem.TermsAndConditions:
        window.open(
          `${environment.authorityPortalUrl}${LegalAgreements.TERMS_PATH}?source=coach-portal`,
          '_blank',
          'noopener',
        );
        break;
      case ProfileMenuItem.PrivacyPolicy:
        window.open(
          `${environment.authorityPortalUrl}${LegalAgreements.PRIVACY_PATH}?source=coach-portal`,
          '_blank',
          'noopener',
        );
        break;
      case ProfileMenuItem.OpenSourceLicenses:
        // Terms and Privacy live on the authority portal; the attribution page is
        // bundled here, so it is an in-app navigation rather than a new tab.
        this.router.navigate([RouteConstants.LICENSES_ROUTE]);
        break;
      case ProfileMenuItem.Logout:
        this.shellService.logout();
        break;
    }
    if ((event.item as any).parentObj.properties.text === ProfileMenuItem.Language) {
      this.shellService.changeLanguage(event.item.text as string);
    }
  }
}
